export const OPTION_CATEGORIES = [
  "test_name", "sample_type", "department", "priority", "status", "tat_stage", "tat_status",
  "rejection_reason", "repeat_reason", "critical_test", "analyzer", "analyte", "qc_level",
  "qc_result", "westgard_rule", "unit", "vendor", "item_category", "item_name", "uom",
  "indent_status", "sop_category", "sop_status", "equipment_activity", "equipment_status",
  "pt_provider", "pt_outcome", "ward", "doctor", "technician", "shift", "yes_no",
  "notified", "action_taken", "sample_nature", "delivered_from", "acknowledged",
];

const f = (name, label, type = "text", opts = {}) => ({ name, label, type, ...opts });

export const MODULES = {
  samples: {
    label: "Sample & Test Records",
    desc: "Register samples, assign tests and track their status end to end.",
    icon: "TestTubes",
    columns: ["sample_id", "patient_name", "test_name", "sample_type", "priority", "status", "critical", "tat_mins", "tat_status"],
    fields: [
      f("sample_id", "Sample ID", "text", { required: true }),
      f("barcode", "Barcode", "text", { hint: "Filled by the scanner — reused on the next scan" }),
      f("patient_name", "Patient Name", "text", { required: true }),
      f("patient_id", "Patient ULID", "ulid", { hint: "Unique LabTrack ID — auto-links this sample to its traceability entry" }),
      f("age", "Age"),
      f("sex", "Sex", "select", { options: ["Male", "Female", "Other"] }),
      f("test_name", "Test", "select", { category: "test_name", required: true }),
      f("sample_type", "Sample Type", "select", { category: "sample_type" }),
      f("department", "Department", "select", { category: "department" }),
      f("ward", "Ward / Location", "select", { category: "ward" }),
      f("doctor", "Referring Doctor", "select", { category: "doctor" }),
      f("priority", "Priority", "select", { category: "priority" }),
      f("collection_date", "Collection Date", "date"),
      f("collection_time", "Collection Time", "time"),
      f("received_time", "Received in Lab", "datetime-local"),
      f("reported_time", "Reported At", "datetime-local"),
      f("target_tat_mins", "Target TAT (mins)", "number"),
      f("tat_mins", "Actual TAT (mins)", "computed", { hint: "Auto-calculated from Received in Lab → Reported At" }),
      f("tat_status", "TAT Status", "computed", { hint: "Auto-set against Target TAT" }),
      f("analyzer", "Analyzer Used", "select", { category: "analyzer" }),
      f("technician", "Performed By", "select", { category: "technician" }),
      f("status", "Status", "select", { category: "status" }),
      f("signed_out_by", "Signed Out By (Pathologist)", "select", { category: "pathologist", hint: "Active consultants and senior residents from Personnel" }),
      f("co_signed_by", "Co-signed By", "select", { category: "pathologist", hint: "Optional second signatory" }),
      f("critical_params", "Critical Findings", "computed", { hint: "Auto-flagged parameters outside critical limits" }),
      f("critical", "Critical Report", "computed", { hint: "Set automatically when a critical limit is crossed" }),
      f("result_summary", "Result Summary", "textarea"),
      f("remarks", "Remarks", "textarea"),
      f("attachment", "Attachment", "file"),
    ],
  },
  tat: {
    label: "TAT Manager",
    desc: "Monitor turnaround time stage-wise and flag breaches.",
    icon: "Timer",
    columns: ["sample_id", "test_name", "tat_stage", "target_tat_mins", "actual_tat_mins", "tat_status"],
    fields: [
      f("sample_id", "Sample ID", "text", { required: true }),
      f("test_name", "Test", "select", { category: "test_name", required: true }),
      f("department", "Department", "select", { category: "department" }),
      f("priority", "Priority", "select", { category: "priority" }),
      f("tat_stage", "Stage", "select", { category: "tat_stage" }),
      f("start_time", "Start Time", "datetime-local"),
      f("end_time", "End Time", "datetime-local"),
      f("target_tat_mins", "Target TAT (mins)", "number"),
      f("actual_tat_mins", "Actual TAT (mins)", "computed", { hint: "Auto-calculated from Start → End Time" }),
      f("tat_status", "TAT Status", "computed", { hint: "Auto-set: Breached / At Risk / Within TAT" }),
      f("delay_reason", "Delay Reason", "textarea"),
      f("technician", "Responsible", "select", { category: "technician" }),
      f("attachment", "Attachment", "file"),
    ],
  },
  critical_alerts: {
    label: "Critical Alerts",
    desc: "Log critical values, notification and read-back details.",
    icon: "Siren",
    columns: ["sample_id", "patient_name", "critical_test", "value", "alert_state", "notified", "notified_to"],
    fields: [
      f("sample_id", "Sample ID", "text", { required: true }),
      f("patient_name", "Patient Name"),
      f("alert_state", "Alert State", "computed", { hint: "Auto-resolved when a corrected value is no longer critical" }),
      f("critical_test", "Critical Parameter", "select", { category: "critical_test", required: true }),
      f("value", "Value", "text"),
      f("unit", "Unit", "select", { category: "unit" }),
      f("alert_datetime", "Alert Date & Time", "datetime-local"),
      f("notified", "Notified", "select", { category: "notified" }),
      f("notified_to", "Notified To", "select", { category: "doctor" }),
      f("notified_by", "Notified By", "select", { category: "technician" }),
      f("read_back", "Read-back Confirmed", "select", { category: "yes_no" }),
      f("time_to_notify_mins", "Time to Notify (mins)", "number"),
      f("action_taken", "Action Taken", "select", { category: "action_taken" }),
      f("remarks", "Remarks", "textarea"),
      f("attachment", "Attachment", "file"),
    ],
  },
  rejections: {
    label: "Sample Rejections",
    desc: "Record rejected samples with reasons and recollection status.",
    icon: "Ban",
    columns: ["sample_id", "test_name", "rejection_reason", "rejected_by", "recollected", "rejection_date"],
    fields: [
      f("sample_id", "Sample ID", "text", { required: true }),
      f("patient_name", "Patient Name"),
      f("test_name", "Test", "select", { category: "test_name" }),
      f("sample_type", "Sample Type", "select", { category: "sample_type" }),
      f("ward", "Source / Ward", "select", { category: "ward" }),
      f("rejection_reason", "Rejection Reason", "select", { category: "rejection_reason", required: true }),
      f("rejection_date", "Rejection Date", "date"),
      f("rejected_by", "Rejected By", "select", { category: "technician" }),
      f("informed_to", "Informed To", "select", { category: "doctor" }),
      f("recollected", "Recollected", "select", { category: "yes_no" }),
      f("corrective_action", "Corrective Action", "textarea"),
      f("attachment", "Attachment", "file"),
    ],
  },
  repeats: {
    label: "Repeat Runs",
    desc: "Track repeat testing, initial vs repeat values and conclusions.",
    icon: "RefreshCw",
    columns: ["sample_id", "test_name", "repeat_reason", "initial_value", "repeat_value", "conclusion"],
    fields: [
      f("sample_id", "Sample ID", "text", { required: true }),
      f("test_name", "Test", "select", { category: "test_name", required: true }),
      f("analyte", "Parameter", "select", { category: "analyte" }),
      f("repeat_reason", "Repeat Reason", "select", { category: "repeat_reason", required: true }),
      f("initial_value", "Initial Value"),
      f("repeat_value", "Repeat Value"),
      f("unit", "Unit", "select", { category: "unit" }),
      f("analyzer", "Analyzer", "select", { category: "analyzer" }),
      f("repeat_date", "Repeat Date", "date"),
      f("performed_by", "Performed By", "select", { category: "technician" }),
      f("conclusion", "Conclusion", "textarea"),
      f("attachment", "Attachment", "file"),
    ],
  },
  qc: {
    label: "Quality Control",
    desc: "Daily internal QC runs with Westgard interpretation.",
    icon: "ShieldCheck",
    columns: ["run_date", "analyzer", "analyte", "qc_level", "result_value", "z_score", "westgard_rule", "qc_result"],
    fields: [
      f("run_date", "Run Date", "date", { required: true }),
      f("shift", "Shift", "select", { category: "shift" }),
      f("analyzer", "Analyzer", "select", { category: "analyzer", required: true }),
      f("serial_number", "Serial Number"),
      f("department", "Department", "select", { category: "department" }),
      f("analyte", "Analyte", "select", { category: "analyte", required: true }),
      f("qc_level", "QC Level", "select", { category: "qc_level" }),
      f("lot_number", "Control Lot No."),
      f("result_value", "Result Value", "number", { required: true }),
      f("unit", "Unit", "select", { category: "unit" }),
      f("target_mean", "Target Mean", "number"),
      f("target_sd", "Target SD", "number"),
      f("westgard_rule", "Westgard Rule Violated", "computed", { hint: "Auto-applied: 1-2s / 1-3s / 2-2s / R-4s / 4-1s / 10x" }),
      f("z_score", "Z-Score", "computed", { hint: "Auto-calculated from Target Mean and SD" }),
      f("qc_result", "QC Outcome", "computed", { hint: "Auto-set from the Westgard evaluation" }),
      f("performed_by", "Performed By", "select", { category: "technician" }),
      f("corrective_action", "Corrective Action", "textarea"),
      f("attachment", "QC Printout", "file"),
    ],
  },
  traceability: {
    label: "Sample Traceability Log",
    desc: "Chain of custody for every sample handed over to the haematology lab.",
    icon: "ScrollText",
    columns: ["uhid", "sample_id", "nature_of_sample", "received_on", "time_of_receipt",
      "delivered_from", "delivered_by", "delivered_to", "attender_ack", "technician_ack"],
    fields: [
      f("uhid", "ULID", "ulid", { required: true, hint: "Unique LabTrack ID — auto-links to the sample record with the same ULID" }),
      f("sample_id", "Sample ID", "text", { hint: "Optional — links this entry to a registered sample" }),
      f("patient_name", "Patient Name", "text", { hint: "Kept in step with the sample record" }),
      f("nature_of_sample", "Nature of Sample", "select", { category: "sample_nature", required: true }),
      f("received_on", "Received On", "date", { required: true }),
      f("time_of_receipt", "Time of Receipt", "time", { required: true }),
      f("delivered_to", "Delivered To (Haematology Technician)", "select", { category: "technician", required: true }),
      f("delivered_from", "Delivered From", "select", { category: "delivered_from", required: true }),
      f("delivered_by", "Delivered By (Hospital Attender)", "text", { required: true, hint: "Name of the attender who handed over the sample" }),
      f("attender_ack", "Verbally Confirmed by Attender", "signature", { role: "attender", hint: "Attender signs on screen at handover" }),
      f("technician_ack", "Verbally Confirmed by Receiving Technician", "signature", { role: "technician" }),
      f("condition_on_receipt", "Condition on Receipt", "textarea"),
      f("remarks", "Remarks", "textarea"),
      f("attachment", "Handover Slip", "file"),
    ],
  },
  sops: {
    label: "SOP Records",
    desc: "Controlled SOP documents with versions, review dates and files.",
    icon: "BookOpen",
    columns: ["sop_code", "sop_title", "sop_category", "version", "sop_status", "next_review_date"],
    fields: [
      f("sop_code", "SOP Code", "text", { required: true }),
      f("sop_title", "SOP Title", "text", { required: true }),
      f("sop_category", "Category", "select", { category: "sop_category" }),
      f("analyzer", "Equipment / Analyzer", "select", { category: "analyzer" }),
      f("serial_number", "Serial Number"),
      f("department", "Department", "select", { category: "department" }),
      f("version", "Version"),
      f("effective_date", "Effective Date", "date"),
      f("next_review_date", "Next Review Date", "date"),
      f("prepared_by", "Prepared By", "select", { category: "technician" }),
      f("approved_by", "Approved By", "select", { category: "doctor" }),
      f("sop_status", "Status", "select", { category: "sop_status" }),
      f("training_done", "Staff Training Done", "select", { category: "yes_no" }),
      f("remarks", "Remarks", "textarea"),
      f("attachment", "SOP Document", "file"),
    ],
  },
  equipment: {
    label: "Equipment Log",
    desc: "Maintenance, calibration and breakdown log for every analyzer.",
    icon: "Wrench",
    columns: ["analyzer", "equipment_activity", "activity_date", "equipment_status", "performed_by"],
    fields: [
      f("analyzer", "Equipment", "select", { category: "analyzer", required: true }),
      f("serial_number", "Serial Number"),
      f("department", "Department", "select", { category: "department" }),
      f("vendor", "Vendor / Supplier", "select", { category: "vendor" }),
      f("equipment_activity", "Activity", "select", { category: "equipment_activity", required: true }),
      f("activity_date", "Activity Date", "date"),
      f("downtime_hours", "Downtime (hours)", "number"),
      f("equipment_status", "Equipment Status", "select", { category: "equipment_status" }),
      f("performed_by", "Performed By", "select", { category: "technician" }),
      f("engineer_name", "Service Engineer"),
      f("next_due_date", "Next Due Date", "date"),
      f("observations", "Observations", "textarea"),
      f("attachment", "Service Report", "file"),
    ],
  },
  proficiency: {
    label: "Proficiency Testing",
    desc: "EQAS / PT cycles, z-scores and outcomes per analyte.",
    icon: "Award",
    columns: ["pt_provider", "cycle", "analyte", "our_value", "z_score", "pt_outcome"],
    fields: [
      f("pt_provider", "PT / EQAS Provider", "select", { category: "pt_provider", required: true }),
      f("cycle", "Cycle / Sample Code", "text", { required: true }),
      f("pt_date", "PT Date", "date"),
      f("analyte", "Analyte", "select", { category: "analyte", required: true }),
      f("our_value", "Our Result", "number"),
      f("assigned_value", "Assigned / Peer Value", "number"),
      f("unit", "Unit", "select", { category: "unit" }),
      f("z_score", "Z-Score / SDI", "number"),
      f("pt_outcome", "Outcome", "select", { category: "pt_outcome" }),
      f("analyzer", "Analyzer", "select", { category: "analyzer" }),
      f("serial_number", "Serial Number"),
      f("department", "Department", "select", { category: "department" }),
      f("performed_by", "Performed By", "select", { category: "technician" }),
      f("root_cause", "Root Cause (if unsatisfactory)", "textarea"),
      f("corrective_action", "Corrective Action", "textarea"),
      f("attachment", "PT Report", "file"),
    ],
  },
  archives: {
    label: "Archived Reports",
    desc: "Historical reports uploaded in bulk — read, extracted and logged automatically.",
    icon: "Archive",
    columns: ["report_date", "report_time", "patient_name", "test_name", "critical", "signed_out_by", "co_signed_by"],
    fields: [
      f("report_date", "Report Date", "date"),
      f("month", "Month", "text"),
      f("year", "Year", "text"),
      f("patient_name", "Patient Name"),
      f("patient_id", "Patient ULID"),
      f("test_name", "Test", "select", { category: "test_name" }),
      f("department", "Department", "select", { category: "department" }),
      f("critical", "Critical Report", "select", { category: "yes_no" }),
      f("signed_out_by", "Signed Out By"),
      f("co_signed_by", "Co-signed By"),
      f("report_time", "Report Time", "time"),
      f("critical_reason", "Critical Finding", "textarea"),
      f("tat_status", "TAT Status", "select", { category: "tat_status" }),
      f("summary", "Summary", "textarea"),
      f("source_file", "Source File"),
      f("attachment", "Original Report", "file"),
    ],
  },
};

export const MODULE_KEYS = Object.keys(MODULES);
